import { FlatTreeControl } from '@angular/cdk/tree';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { ICostingLevel } from '../../../models/costing-level.model';
import { CostingLevelTypeEnum } from '../../../models/enum/costing-level-type.enum';
import { ICostingLevelLayout } from '../../../models/layout/costing-level-layout.model';
import { ICostingLevelNode } from '../../../models/util/kalkulationsstufe-node.model';
import { ISharedDataState } from '../../../services/facades/master-data/shared-data-state.model';
import { SharedDataFacade } from '../../../services/facades/master-data/shared-data.facade';
import { CostingLevelLayoutService } from '../../../services/layout/costing-level-layout.service';
import { CostingLevelDialogComponent } from './costing-level-dialog/costing-level-dialog.component';
import { distinctUntilChanged, firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { ICostingLevelsState } from '../../../services/facades/master-data/costing-levels-state.model';
import { CostingLevelsFacade } from '../../../services/facades/master-data/costing-levels.facade';
import { ConfirmDialogComponent } from '@sla-ng/ui-material';


@Component({
    templateUrl: './costing-level.page.html',
    styleUrls: [ './costing-level.page.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CostingLevelPage implements OnInit, OnDestroy {

    public treeControl: FlatTreeControl<ICostingLevelNode>;
    public treeFlattener: MatTreeFlattener<ICostingLevelNode, ICostingLevel>;
    public isExpanded: boolean = true;

    public dataSource: MatTreeFlatDataSource<ICostingLevelNode, ICostingLevel>;

    public layout: ICostingLevelLayout;

    public costingLevelState$: Observable<ICostingLevelsState>;
    public sharedDataState$: Observable<ISharedDataState>;

    private destroy$: Subject<void> = new Subject();
    private originalData: ICostingLevelNode[] = [];
    public transformer = (node: ICostingLevelNode, level: number): ICostingLevelNode => {
        const transformed = node;
        transformed.expandable = !!node.children && node.children.length > 0;
        transformed.level = level;
        return transformed;
    };

    // eslint-disable-next-line @typescript-eslint/member-ordering
    constructor(private myCostingLevelFacade: CostingLevelsFacade,
                private myMatDialog: MatDialog,
                private mySharedDataFacade: SharedDataFacade,
                private myCostingLevelLayoutService: CostingLevelLayoutService) {
        this.treeControl = new FlatTreeControl<ICostingLevelNode>(node => node.level, node => node.expandable);
        this.layout = this.myCostingLevelLayoutService.getCostingLevelDialogLayout();
        this.treeFlattener = new MatTreeFlattener(
            this.transformer, node => node.level,
            node => node.expandable, node => node.children);
        this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    }

    public ngOnInit(): void {
        this.costingLevelState$ = this.myCostingLevelFacade.subState();
        this.sharedDataState$ = this.mySharedDataFacade.subState();
        this.sharedDataState$
            .pipe(
                takeUntil(this.destroy$),
                map((s: ISharedDataState) => s.costingLevelTree),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((tree: ICostingLevelNode[]) => {

                // This is the order in which they should appear
                const order: string[] = [
                    CostingLevelTypeEnum.DIRECT_MATERIAL_COST.valueOf(),
                    CostingLevelTypeEnum.INDIRECT_MATERIAL_COST.valueOf(),
                    CostingLevelTypeEnum.DIRECT_PRODUCTION_COST.valueOf(),
                    CostingLevelTypeEnum.INDIRECT_PRODUCTION_COST.valueOf(),
                    CostingLevelTypeEnum.INDIRECT_ADMINISTRATION_COST.valueOf(),
                    CostingLevelTypeEnum.INDIRECT_DISTRIBUTION_COST.valueOf(),
                    null,
                ];

                tree.sort((a, b) => order.indexOf(a.costingLevelType) - order.indexOf(b.costingLevelType));
                this.originalData = tree;
                this.dataSource.data = tree;
                if (this.isExpanded) {
                    this.treeControl.expandAll();
                }
            });
        void firstValueFrom(this.mySharedDataFacade.fetchCostingLevels());
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public onEditKalkstufe(kalkstufe: ICostingLevelNode): void {
        const dialog: MatDialogRef<CostingLevelDialogComponent> = this.myMatDialog.open(CostingLevelDialogComponent, {
            width: '600px',
            data: kalkstufe as ICostingLevel,
        });
        dialog.afterClosed().subscribe((update: boolean) => {
            if (update) {
                void firstValueFrom(this.mySharedDataFacade.fetchCostingLevels(true));
            }
        });
    }

    public onDeleteCostingLevel(costingLevel: ICostingLevel): void {
        const dialog: MatDialogRef<ConfirmDialogComponent> = this.myMatDialog.open(ConfirmDialogComponent, {
            width: '600px',
            data: {
                message: 'app.page.costing-level.confirm-delete.label',
                messageParams: { name: costingLevel.name },
            },
        });
        dialog.afterClosed().subscribe(async (isConfirmed: boolean) => {
            if (isConfirmed) {
                await this.myCostingLevelFacade.deleteCostingLevel(costingLevel.id);
                void firstValueFrom(this.mySharedDataFacade.fetchCostingLevels(true));
            }
        });
    }

    public onAddKalkstufe(kalkstufeParent?: ICostingLevelNode): void {
        const dialog: MatDialogRef<CostingLevelDialogComponent> = this.myMatDialog.open(CostingLevelDialogComponent, {
            width: '600px',
            data: kalkstufeParent ? { parentCostingLevelId: kalkstufeParent.id } : {},
        });
        dialog.afterClosed().subscribe((update: boolean) => {
            if (update) {
                void firstValueFrom(this.mySharedDataFacade.fetchCostingLevels(true));
            }
        });
    }

    // performs filtering
    public applyFilter(filter: string): void {
        filter = filter.trim().toLowerCase();
        if ( filter==='' || filter=== ' '){
            this.dataSource.data = this.originalData;
        } else {
            const filteredData = this.filterTreeData(this.originalData, filter);
            this.dataSource.data = filteredData;
        }
    }

    public expandAll(): void {
        this.treeControl.expandAll();
        this.isExpanded = true;
    }

    public collapseAll(): void {
        this.treeControl.collapseAll();
        this.isExpanded = false;
    }

    // filters through tree nodes based on filter
    private filterTreeData( data: ICostingLevelNode[], filter: string ): ICostingLevelNode[]{
        return data
            .map(node => this.filterNode(node, filter))
            .filter(node => node != null ) ;
    }

    // filters through node and node children
    private filterNode(node: ICostingLevelNode, filter: string): ICostingLevelNode | null {
        // node matches filter -> return node
        const nodeMatches = this.nodeMatchesFilter(node, filter);

        if (nodeMatches) {
            return node;
        }

        // node = parent node? check children
        if (node.children) {
            const filteredChildren = node.children
                .map(child => this.filterNode(child, filter))
                .filter(child => child !== null);
            // children match filter? crete new node with children
            if (filteredChildren.length > 0) {
                return {
                    ...node,
                    children: filteredChildren
                };
            }
        }

        return null;
    }

    // node matches filter ?
    private nodeMatchesFilter(node: ICostingLevelNode, filter: string): boolean {
        return JSON.stringify(node).toLowerCase().includes(filter);
    }
}
